import React from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

import Icon from '../../../components/AppIcon';
import { FilterOptions } from '../types';

interface FilterControlsProps {
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  onRefresh: () => void;
}

const FilterControls = ({ filters, onFiltersChange, onRefresh }: FilterControlsProps) => {
  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'dining', label: 'Dining & Restaurants' },
    { value: 'attractions', label: 'Tourist Attractions' },
    { value: 'shopping', label: 'Shopping & Retail' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'transport', label: 'Transportation' },
    { value: 'accommodation', label: 'Hotels & Accommodation' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Vienna Districts' },
    { value: 'innere-stadt', label: 'Innere Stadt (1st District)' },
    { value: 'leopoldstadt', label: 'Leopoldstadt (2nd District)' },
    { value: 'landstrasse', label: 'Landstraße (3rd District)' },
    { value: 'wieden', label: 'Wieden (4th District)' },
    { value: 'margareten', label: 'Margareten (5th District)' },
    { value: 'mariahilf', label: 'Mariahilf (6th District)' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' },
    { value: 'expired', label: 'Expired' }
  ];

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const resetFilters = () => {
    onFiltersChange({
      category: 'all',
      location: 'all',
      status: 'all',
      dateRange: 'month'
    });
  };

  return (
    <div className="bg-surface rounded-lg border border-border p-6 shadow-sm mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Filters & Controls</h3>
          <p className="text-sm text-muted-foreground">Customize your offer analytics view</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={resetFilters}>
            Reset Filters
          </Button>
          <Button variant="outline" size="sm" onClick={onRefresh} iconName="RefreshCw" iconPosition="left">
            Refresh Data
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Select
          label="Category"
          options={categoryOptions}
          value={filters.category}
          onChange={(value) => handleFilterChange('category', value as string)}
          placeholder="Select category"
        />

        <Select
          label="Location"
          options={locationOptions}
          value={filters.location}
          onChange={(value) => handleFilterChange('location', value as string)}
          placeholder="Select location"
        />

        <Select
          label="Status"
          options={statusOptions}
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value as string)}
          placeholder="Select status"
        />

        <Select
          label="Date Range"
          options={dateRangeOptions}
          value={filters.dateRange}
          onChange={(value) => handleFilterChange('dateRange', value as string)}
          placeholder="Select date range"
        />
      </div>

      {/* Quick Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Filter" size={14} />
            <span>Active filters: {Object.values(filters).filter(v => v !== 'all').length}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={14} />
            <span>Last updated: {new Date().toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              timeZone: 'Europe/Vienna'
            })} CET</span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export Data
          </Button>
          <Button variant="outline" size="sm" iconName="Settings" iconPosition="left">
            View Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterControls;